import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from "@angular/core";
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterOutlet,
} from "@angular/router";
import { logger } from "../../util/Logger";
import { slideInAnimation } from "../../util/animations";
import { AuthService } from "../../services/auth.service";
import { SubscriptionGroup } from "../../util/subscriptionGroup";
import { SecurityService } from "../../services/security.service";
import { NotificationsService } from "angular2-notifications";
import {
  combineLatest,
  debounceTime,
  filter,
  first,
  map,
  mergeMap,
} from "rxjs";
import { SessionApi } from "../../api/session.api";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { AuthApi } from "../../api/auth.api";
import { WebSocketService } from "../../services/webSocket.service";
import { VersionService } from "../../services/core/version.service";
import { LoadingService } from "../../services/core/loading.service";
import { GoogleAnalyticsService } from "ngx-google-analytics";
import { environment } from "src/environments/environment";
import { Meta, Title } from "@angular/platform-browser";
import { StorageServiceKey } from "../../services/storage.service";
import { TourService } from "../../services/tour.service";

const className = "MainTemplateComponent";

@Component({
  moduleId: module.id,
  animations: [slideInAnimation],
  selector: "app-main-template",
  templateUrl: "main-template.component.html",
  styleUrls: [],
})
export class MainTemplateComponent implements OnInit, OnDestroy {
  public sessionReady: boolean = false;

  year: string = new Date().getFullYear().toString();
  private readonly subscriptionGroup = new SubscriptionGroup();

  @ViewChild("modalContainer", {
    read: ViewContainerRef,
  })
  modalContainer: ViewContainerRef;

  @ViewChild("tfa_model") tfa_model;
  modalReference: any;

  @ViewChild("tour_model") tour_model;

  existingNotification: any = null;
  notificationShown: boolean = false;
  loading = false;

  constructor(
    private securityService: SecurityService,
    private notifications: NotificationsService,
    public authenticationService: AuthService,
    private session: SessionApi,
    private __modalService: NgbModal,
    private router: Router,
    private authApi: AuthApi,
    private websocketService: WebSocketService,
    private versionService: VersionService,
    private loaderService: LoadingService,
    private cdr: ChangeDetectorRef,
    protected gaService: GoogleAnalyticsService,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    private metaService: Meta,
    private tourService: TourService
  ) {
    this.websocketService.initiateConnection();
    this.versionService.monitorAppVersion();
    this.loaderService.loading$.subscribe((isLoading) => {
      setTimeout(() => {
        this.loading = isLoading;
      });
      this.cdr.markForCheck();
    });

    this.setDynamicData();
  }

  getStarted() {
		this.tourService.startTour();
    this.router.navigate(["/manage/dashboard"]);
    this.__modalService.dismissAll();
  }
  

  setDynamicData() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map((route) => {
          while (route.firstChild) route = route.firstChild;
          return route;
        }),
        filter((route) => route.outlet === "primary"),
        mergeMap((route) => route.data)
      )
      .subscribe((data: { title: string; description: string }) => {
        if (data) {
          const pageTitle = data.title
            ? `${data.title}`
            : "Reali - Because Uniforms Matter";
          this.titleService.setTitle(pageTitle);

          if (data.description) {
            this.metaService.updateTag({
              name: "description",
              content: data.description,
            });
          }
        }
      });
  }

  ngOnInit() {
    this.blurNumberInputOnWheel();

    // This starts the session- The user will see nothing until this is complete
    this.session.init();

    this.session.$sessionChanged.pipe(filter((ready) => ready)).subscribe({
      next: () => {
        this.sessionReady = true;

        const isShowTour = localStorage.getItem(StorageServiceKey.show_tour);
        if (isShowTour == "true") {
          this.router.events
            .pipe(
              filter((evt) => evt instanceof NavigationEnd),
              first()
            )
            .subscribe(() => {
              const modalRef = this.__modalService.open(this.tour_model, {
                size: "sm",
                modalDialogClass: "modal-dialog-centered",
                windowClass: "bulk-order",
              });

              modalRef.result.finally(() => {
                localStorage.setItem(StorageServiceKey.show_tour, "false");
                this.__modalService.dismissAll();
              });
            });
        } else {
          if (
            this.session.$userData.value &&
            !this.session.$userData.value?.isAdmin
          ) {
            setTimeout(() => {
              if (this.router.url === "" || this.router.url === "/") {
                this.authenticationService.navigateToDefaultUrl();
              }
            }, 1000);
          }
        }
        this.checkCredit();
        this.checkObsolete();
      },
    });

    this.monitor2faEnabled();

    this.unregisterServiceWorkers();

    this.trackAnalytics();
  }

  trackAnalytics() {
    this.router.events
      .pipe(
        filter((evt) => evt instanceof NavigationEnd),
        debounceTime(100)
      )
      .subscribe((event: NavigationEnd) => {
        const finalURL = event.urlAfterRedirects || event.url;

        this.gaService.gtag("event", "page_view", {
          page_location: finalURL,
        });
      });
  }

  unregisterServiceWorkers() {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistrations().then(function (registrations) {
        //returns installed service workers
        if (registrations.length) {
          for (let registration of registrations) {
            registration.unregister();
          }
        }
      });
    }
  }

  prepareRoute(outlet: RouterOutlet, data: string) {
    return (
      outlet && outlet.activatedRouteData && outlet.activatedRouteData[data]
    );
  }

  blurNumberInputOnWheel() {
    const signature = className + ".blurNumberInput: ";
    document.addEventListener("wheel", function () {
      if (
        document.activeElement &&
        document.activeElement["type"] &&
        document.activeElement["type"] === "number" &&
        document.activeElement["blur"]
      ) {
        logger.silly(signature + "Prevented mWheel in Number Input");
        document.activeElement["blur"]();
      }
    });
  }

  private checkCredit() {
    this.subscriptionGroup.add(
      this.securityService.isStopCredit().subscribe((obsolete) => {
        if (obsolete && !this.notificationShown) {
          this.notifications.error(
            "Account on hold",
            "Your account is currently on stop credit, you cannot place orders at this time."
          );
          this.notificationShown = true;
        }
      })
    );
  }

  private checkObsolete() {
    this.subscriptionGroup.add(
      this.securityService.isObsolete().subscribe((obsolete) => {
        if (obsolete && !this.notificationShown) {
          this.notifications.error(
            "Account on hold",
            "Your account is currently obsolete, you cannot place orders at this time."
          );
          this.notificationShown = true;
        }
      })
    );
  }

  /**
   * @description Monitors for a user pass auth request. Recommends enabling 2fa exactly once, if not disabled at the customer level
   */
  monitor2faEnabled() {
    this.subscriptionGroup.add(
      combineLatest(
        this.authenticationService.$userPassAuthRequested,
        this.session.$userData
      )
        .pipe(
          filter(([isUserPassAuth, data]) => {
            return !!isUserPassAuth && !!data;
          }),
          map(([isUserPassAuth, data]) => data),
          filter(
            (data) => data?.secureLoginType === "none" || !data?.secureLoginType
          ),
          first(),
          filter(() => {
            const customerData = this.session.$customerData.value;
            return !customerData || !customerData.disable2faCheck;
          })
        )
        .subscribe((data) => {
          this.modalReference = this.__modalService.open(this.tfa_model, {
            size: "sm",
            modalDialogClass: "modal-dialog-centered",
            windowClass: "bulk-order",
          });
        })
    );
  }

  enable2fa() {
    this.modalReference.close();
    this.authApi.getInformation().subscribe((user) => {
      this.session.$userData.getValue();
      if (user && user.isAdmin) {
        this.router.navigate(["/manage/users/admin/edit", user.id]);
      } else if (user && user.customerUsers) {
        this.router.navigate([
          "/account/details",
          this.session.$customerUser.value?.id,
          "customer",
          user.customerUsers[0].customerId,
        ]);
      }
    });
  }

  close() {
    this.modalReference.close();
  }

  @HostListener("document:click", ["$event"])
  onClick() {
    if (this.modalReference) {
      this.modalReference.close();
    }
  }

  closeTourModal() {
    localStorage.setItem(StorageServiceKey.show_tour, "false");
    this.__modalService.dismissAll();
  }

  ngOnDestroy() {
    this.subscriptionGroup.unsubscribe();
  }
}
